import {
	type Condition,
	type Operator,
	type Stat,
	statToDisplay,
	type Window,
} from "@/lib/api";
import { entityName, type Players, type Teams } from "@/lib/entities";

export const COMPARISON_LABEL: Record<Operator, string> = {
	eq: "exactly",
	ne: "not exactly",
	lt: "fewer than",
	lte: "at most",
	gt: "more than",
	gte: "at least",
};

const STAT_LABEL: Record<Stat, string> = {
	avg: "batting average",
	hr: "home runs",
	rbi: "RBI",
	obp: "on-base percentage",
	slg: "slugging percentage",
};

export const statLabel = (stat: Stat | null): string =>
	stat ? STAT_LABEL[stat] : "a stat";

export const statAbbr = (stat: Stat | null): string =>
	stat ? statToDisplay(stat) : "—";

const TEAM_ABBRS: Record<string, string> = {
	"arizona diamondbacks": "ARI",
	athletics: "ATH",
	"atlanta braves": "ATL",
	"baltimore orioles": "BAL",
	"boston red sox": "BOS",
	"chicago cubs": "CHC",
	"chicago white sox": "CWS",
	"cincinnati reds": "CIN",
	"cleveland guardians": "CLE",
	"colorado rockies": "COL",
	"detroit tigers": "DET",
	"houston astros": "HOU",
	"kansas city royals": "KC",
	"los angeles angels": "LAA",
	"los angeles dodgers": "LAD",
	"miami marlins": "MIA",
	"milwaukee brewers": "MIL",
	"minnesota twins": "MIN",
	"new york mets": "NYM",
	"new york yankees": "NYY",
	"philadelphia phillies": "PHI",
	"pittsburgh pirates": "PIT",
	"san diego padres": "SD",
	"san francisco giants": "SF",
	"seattle mariners": "SEA",
	"st. louis cardinals": "STL",
	"tampa bay rays": "TB",
	"texas rangers": "TEX",
	"toronto blue jays": "TOR",
	"washington nationals": "WSH",
};

const TEAM_NICKNAMES: Record<string, string> = {
	angels: "LAA",
	astros: "HOU",
	athletics: "ATH",
	braves: "ATL",
	brewers: "MIL",
	cardinals: "STL",
	cubs: "CHC",
	diamondbacks: "ARI",
	dodgers: "LAD",
	giants: "SF",
	guardians: "CLE",
	mariners: "SEA",
	marlins: "MIA",
	mets: "NYM",
	nationals: "WSH",
	orioles: "BAL",
	padres: "SD",
	phillies: "PHI",
	pirates: "PIT",
	rangers: "TEX",
	rays: "TB",
	reds: "CIN",
	redsox: "BOS",
	rockies: "COL",
	royals: "KC",
	tigers: "DET",
	twins: "MIN",
	"white sox": "CWS",
	yankees: "NYY",
	"blue jays": "TOR",
};

export const teamAbbr = (name: string): string => {
	const normalized = name.trim().toLowerCase();

	return (
		TEAM_ABBRS[normalized] ??
		TEAM_NICKNAMES[normalized.split(" ").slice(-1)[0]] ??
		name.slice(0, 3).toUpperCase()
	);
};

export const windowPhrase = (
	window: Window | null,
	over: number | null,
): string => {
	if (window === "inning") {
		return over && over > 1 ? `in ${over} consecutive innings` : "in an inning";
	}

	return "in a game";
};

export const flattenLeaves = (condition: Condition): Condition[] => {
	if (condition.ruleType === "leaf") {
		return [condition];
	}

	return condition.rules.flatMap(flattenLeaves);
};

export const hasValue = (condition: Condition): boolean =>
	condition.value !== null && condition.value !== undefined;

export const conditionToPrompt = (
	condition: Condition,
	players: Players,
	teams: Teams,
): string => {
	const leafToText = (leaf: Condition): string => {
		const subject =
			entityName(leaf.entityType, leaf.entityID, players, teams) ?? "someone";
		const verb = leaf.entityType === "team" ? "have" : "has";
		const value = hasValue(leaf) ? leaf.value : "a number";

		return `${subject} ${verb} ${COMPARISON_LABEL[leaf.operator ?? "gte"]} ${value} ${statLabel(leaf.stat)} ${windowPhrase(leaf.window, leaf.over)}`;
	};

	const walk = (node: Condition): string => {
		if (node.ruleType === "leaf") {
			return leafToText(node);
		}

		return node.rules.map(walk).join(` ${node.ruleType} `);
	};

	return `When ${walk(condition)}`;
};
